/**
 * Soundium "Notify me" / stock-info modals (SND-11163, SND-11190, SND-11197).
 *
 * The store runs Shopify spam protection (invisible hCaptcha) on contact forms.
 * Programmatic submits (fetch, or a native submit routed into a hidden iframe)
 * break Shopify's captcha flow and get rejected with "Missing CAPTCHA token".
 * So we keep the plain NATIVE submit: Shopify injects + validates the captcha
 * token, the request is sent, and the page reloads to ?contact_posted=true with
 * the snippet server-rendering the success (or error) step.
 *
 * That reload closes the modal, so without this module the customer lands back
 * on a silent product page with no confirmation at all (SND-14066). Here we
 * reopen the dialog on whichever step the server rendered, then strip
 * ?contact_posted from the URL so a refresh doesn't replay the confirmation.
 *
 * NOTE: long-term this form is expected to be replaced by a dedicated embedded
 * Shopify Form that handles its own spam protection + request routing.
 */

const DIALOG_SELECTOR = '.snd-notify-dc';

function stepsOf(dc) {
  return {
    formView: dc.querySelector('.snd-notify__form-view'),
    successView: dc.querySelector('.snd-notify__success-view'),
  };
}

function resetToForm(dc) {
  const { formView, successView } = stepsOf(dc);
  if (successView && !successView.hidden) successView.hidden = true;
  if (formView && formView.hidden) formView.hidden = false;
}

function showSuccess(dc) {
  const { formView, successView } = stepsOf(dc);
  if (formView) formView.hidden = true;
  if (successView) successView.hidden = false;
}

/**
 * Open the dialog once its custom element has been upgraded. `whenDefined`
 * resolves at definition time, but the upgrade of an already-parsed element is
 * scheduled, so retry across a few frames before giving up.
 */
function openDialog(dc, attemptsLeft = 10) {
  customElements.whenDefined('dialog-component').then(() => {
    requestAnimationFrame(() => {
      if (typeof dc.showDialog === 'function') dc.showDialog();
      else if (attemptsLeft > 0) openDialog(dc, attemptsLeft - 1);
    });
  });
}

/**
 * Shopify redirects to <page>?contact_posted=true#<form id>, so the fragment
 * tells us WHICH form was submitted. Only used to keep other notify forms on
 * the page pristine — `form.posted_successfully?` is true for every contact
 * form once the param is set.
 */
function submittedFormId() {
  const id = window.location.hash.slice(1);
  if (!id) return null;
  const el = document.getElementById(id);
  return el && el.classList.contains('snd-notify__form') ? id : null;
}

function stripPostedParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('contact_posted')) return;
  url.searchParams.delete('contact_posted');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
}

/** Reopen the modal on the step the server rendered after a native submit. */
function restorePostSubmitStep() {
  const submitted = submittedFormId();
  let opened = false;

  for (const dc of document.querySelectorAll(DIALOG_SELECTOR)) {
    const form = dc.querySelector('.snd-notify__form');

    // A different notify form on this page was the one submitted — leave this
    // one on its pristine form step.
    if (submitted && form && form.id !== submitted) {
      resetToForm(dc);
      continue;
    }

    const { successView } = stepsOf(dc);
    const posted = successView && !successView.hidden;
    const failed = dc.querySelector('.snd-notify__error[data-server-error]');
    if (!posted && !failed) continue;

    if (posted) showSuccess(dc);
    if (!opened) {
      openDialog(dc);
      opened = true;
    }
  }

  if (opened) stripPostedParam();
}

/**
 * If the spam-protection handshake stalls, the native submit never fires and the
 * button would sit on "Sending…" forever. Hand the form back to the customer
 * with a visible message instead of leaving them staring at a dead button.
 */
const STALLED_SUBMIT_MS = 20000;

function releaseStalledSubmit(form, btn, label) {
  if (!btn.hasAttribute('aria-busy')) return;
  btn.removeAttribute('aria-busy');
  btn.textContent = label;

  const inner = form.closest('.snd-notify__inner');
  const message = inner && inner.dataset.errorText;
  if (!message || form.querySelector('.snd-notify__error[data-stalled]')) return;

  const notice = document.createElement('div');
  notice.className = 'snd-notify__error';
  notice.setAttribute('data-stalled', '');
  notice.setAttribute('role', 'alert');
  notice.tabIndex = -1;
  notice.appendChild(document.createTextNode(message));
  form.prepend(notice);
  notice.focus();
}

// Non-blocking feedback — never preventDefault, so Shopify's captcha + native
// submit run exactly as they do for a normal contact form.
document.addEventListener('submit', (event) => {
  const form = event.target instanceof Element ? event.target.closest('.snd-notify__form') : null;
  if (!form) return;
  const btn = form.querySelector('.snd-notify__submit');
  if (!btn || (typeof form.checkValidity === 'function' && !form.checkValidity())) return;

  const label = btn.textContent;
  const stalled = form.querySelector('.snd-notify__error[data-stalled]');
  if (stalled) stalled.remove();

  btn.setAttribute('aria-busy', 'true');
  if (btn.dataset.loading) btn.textContent = btn.dataset.loading;
  window.setTimeout(() => releaseStalledSubmit(form, btn, label), STALLED_SUBMIT_MS);
});

// Re-opening the notify pill or the supplier-stock ETA info chip (SND-11197)
// should always start on the form step, not a stale post-submit success view.
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element
    ? event.target.closest('.snd-notify, .snd-info-chip')
    : null;
  if (!trigger) return;
  const dc = trigger.closest('.snd-notify-dc');
  if (dc) resetToForm(dc);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', restorePostSubmitStep, { once: true });
} else {
  restorePostSubmitStep();
}
